<template>
  <Teleport to="body">
    <div
      v-if="emphasis.length > 0"
      class="fixed inset-0 z-[100]"
      @click="emphasis = []"
    >
      <!-- SVG mask definition -->
      <svg class="size-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <mask id="emphasis-mask">
            <!-- White background covers everything -->
            <rect width="100" height="100" fill="white" />

            <!-- Black paths create holes for emphasized elements -->
            <path
              v-for="(cutout, index) in cutouts"
              :key="index"
              :d="generateRoundedRectPath(cutout)"
              fill="black"
            />
          </mask>
        </defs>

        <!-- Apply mask to the overlay -->
        <rect
          width="100"
          height="100"
          fill="rgba(255, 255, 255, 0.8)"
          class="fill-white/80 dark:fill-black/80"
          mask="url(#emphasis-mask)"
        />

        <!-- Blue outlines for emphasized elements -->
        <path
          v-for="(cutout, index) in outlineCutouts"
          :key="`outline-${index}`"
          :d="generateRoundedRectPath(cutout)"
          fill="none"
          stroke="oklch(77.7% 0.152 181.912)"
          stroke-width="2"
          vector-effect="non-scaling-stroke"
        />
      </svg></div
  ></Teleport>
</template>

<script setup lang="ts">
import { onWatcherCleanup, ref, watch } from "vue";

import { getScrollParent } from "@/lib/dom";

const DRAWER_HEADER_HEIGHT = "53px";

const emphasis = ref(
  new URLSearchParams(window.location.search).getAll("emphasis") || []
);

interface Cutout {
  x: number;
  y: number;
  width: number;
  height: number;
  topLeftRadiusX: number;
  topLeftRadiusY: number;
  topRightRadiusX: number;
  topRightRadiusY: number;
  bottomRightRadiusX: number;
  bottomRightRadiusY: number;
  bottomLeftRadiusX: number;
  bottomLeftRadiusY: number;
}

const cutouts = ref<Cutout[]>([]);
const outlineCutouts = ref<Cutout[]>([]);

// Parse border-radius value, handling "none" and invalid values
const getRadiusValue = (value: string): number => {
  if (!value || value === "none" || value === "0px") {
    return 0;
  }

  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

// Generate SVG path data for a rounded rectangle with individual corner radii
const generateRoundedRectPath = (cutout: Cutout): string => {
  const {
    x,
    y,
    width,
    height,
    topLeftRadiusX,
    topLeftRadiusY,
    topRightRadiusX,
    topRightRadiusY,
    bottomRightRadiusX,
    bottomRightRadiusY,
    bottomLeftRadiusX,
    bottomLeftRadiusY,
  } = cutout;

  // Clamp radii to not exceed half the width or height
  const maxRadiusX = width / 2;
  const maxRadiusY = height / 2;

  const tlX = Math.min(topLeftRadiusX, maxRadiusX);
  const tlY = Math.min(topLeftRadiusY, maxRadiusY);
  const trX = Math.min(topRightRadiusX, maxRadiusX);
  const trY = Math.min(topRightRadiusY, maxRadiusY);
  const brX = Math.min(bottomRightRadiusX, maxRadiusX);
  const brY = Math.min(bottomRightRadiusY, maxRadiusY);
  const blX = Math.min(bottomLeftRadiusX, maxRadiusX);
  const blY = Math.min(bottomLeftRadiusY, maxRadiusY);

  return `
    M ${x + tlX} ${y}
    L ${x + width - trX} ${y}
    ${trX > 0 || trY > 0 ? `A ${trX} ${trY} 0 0 1 ${x + width} ${y + trY}` : ""}
    L ${x + width} ${y + height - brY}
    ${
      brX > 0 || brY > 0
        ? `A ${brX} ${brY} 0 0 1 ${x + width - brX} ${y + height}`
        : ""
    }
    L ${x + blX} ${y + height}
    ${
      blX > 0 || blY > 0 ? `A ${blX} ${blY} 0 0 1 ${x} ${y + height - blY}` : ""
    }
    L ${x} ${y + tlY}
    ${tlX > 0 || tlY > 0 ? `A ${tlX} ${tlY} 0 0 1 ${x + tlX} ${y}` : ""}
    Z
  `
    .replace(/\s+/g, " ")
    .trim();
};

// Parse border-radius values from computed styles and clamp them appropriately
const getBorderRadii = (style: CSSStyleDeclaration, rect: DOMRect) => {
  // Parse individual corner border-radius values
  const topLeft = getRadiusValue(style.borderTopLeftRadius);
  const topRight = getRadiusValue(style.borderTopRightRadius);
  const bottomRight = getRadiusValue(style.borderBottomRightRadius);
  const bottomLeft = getRadiusValue(style.borderBottomLeftRadius);

  // For very large border-radius (like Tailwind's rounded-full with 9999px),
  // clamp to the maximum possible radius for the element's dimensions
  const maxR = Math.min(rect.width, rect.height) / 2;

  return {
    topLeft: Math.min(topLeft, maxR),
    topRight: Math.min(topRight, maxR),
    bottomRight: Math.min(bottomRight, maxR),
    bottomLeft: Math.min(bottomLeft, maxR),
  };
};

// Create cutout data for SVG mask
const createCutouts = () => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const newCutouts: Cutout[] = [];
  const newOutlineCutouts: Cutout[] = [];

  // Convert 1px (half stroke width) to percentage for inset
  const insetX = (1 / viewportWidth) * 100;
  const insetY = (1 / viewportHeight) * 100;

  for (const e of emphasis.value) {
    const selector = `[data-emphasis-identifier="${e}"]`;

    for (const el of document.querySelectorAll(selector)) {
      const rect = el.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(el as Element);

      // Parse border-radius values
      const radius = getBorderRadii(computedStyle, rect);

      // Use exact element bounds
      const x = rect.left;
      const y = rect.top;
      const width = rect.width;
      const height = rect.height;

      // Convert to percentages for SVG viewBox (0-100)
      // Note: radii need separate X and Y values because the viewBox uses
      // preserveAspectRatio="none", so horizontal and vertical scales differ
      const cutout: Cutout = {
        x: (x / viewportWidth) * 100,
        y: (y / viewportHeight) * 100,
        width: (width / viewportWidth) * 100,
        height: (height / viewportHeight) * 100,
        topLeftRadiusX: (radius.topLeft / viewportWidth) * 100,
        topLeftRadiusY: (radius.topLeft / viewportHeight) * 100,
        topRightRadiusX: (radius.topRight / viewportWidth) * 100,
        topRightRadiusY: (radius.topRight / viewportHeight) * 100,
        bottomRightRadiusX: (radius.bottomRight / viewportWidth) * 100,
        bottomRightRadiusY: (radius.bottomRight / viewportHeight) * 100,
        bottomLeftRadiusX: (radius.bottomLeft / viewportWidth) * 100,
        bottomLeftRadiusY: (radius.bottomLeft / viewportHeight) * 100,
      };

      newCutouts.push(cutout);

      // Create outset outline, but inset only edges that would go out of bounds
      let outlineX = cutout.x - insetX;
      let outlineY = cutout.y - insetY;
      let outlineWidth = cutout.width + insetX * 2;
      let outlineHeight = cutout.height + insetY * 2;

      // Check bounds and adjust only problematic edges
      if (outlineX < 0) {
        outlineWidth += outlineX; // Reduce width by the amount we're over
        outlineX = 0;
      }
      if (outlineY < 0) {
        outlineHeight += outlineY; // Reduce height by the amount we're over
        outlineY = 0;
      }
      if (outlineX + outlineWidth > 100) {
        outlineWidth = 100 - outlineX;
      }
      if (outlineY + outlineHeight > 100) {
        outlineHeight = 100 - outlineY;
      }

      const outlineCutout: Cutout = {
        x: outlineX,
        y: outlineY,
        width: Math.max(0, outlineWidth),
        height: Math.max(0, outlineHeight),
        topLeftRadiusX: cutout.topLeftRadiusX,
        topLeftRadiusY: cutout.topLeftRadiusY,
        topRightRadiusX: cutout.topRightRadiusX,
        topRightRadiusY: cutout.topRightRadiusY,
        bottomRightRadiusX: cutout.bottomRightRadiusX,
        bottomRightRadiusY: cutout.bottomRightRadiusY,
        bottomLeftRadiusX: cutout.bottomLeftRadiusX,
        bottomLeftRadiusY: cutout.bottomLeftRadiusY,
      };

      newOutlineCutouts.push(outlineCutout);
    }
  }

  cutouts.value = newCutouts;
  outlineCutouts.value = newOutlineCutouts;
};

watch(
  emphasis,
  (newEmphasis) => {
    if (newEmphasis.length === 0) {
      cutouts.value = [];
      outlineCutouts.value = [];
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;
    onWatcherCleanup(() => controller.abort());

    const initializeEmphasis = () => {
      // Handle scrolling to emphasized elements
      const firstElement = document.querySelector<HTMLElement>(
        `[data-emphasis-identifier="${newEmphasis.at(0)!}"]`
      );
      if (!firstElement) return;

      const lastElement = document.querySelector<HTMLElement>(
        `[data-emphasis-identifier="${newEmphasis.at(-1)!}"]`
      );

      const isInDrawer = !!firstElement.closest(".\\@container\\/drawer");
      let originalScrollMargin = "";
      if (isInDrawer) {
        originalScrollMargin = firstElement.style.scrollMarginTop;
        firstElement.style.scrollMarginTop = DRAWER_HEADER_HEIGHT;
      }

      const wrapper = getScrollParent(firstElement);
      const lastWrapper = lastElement ? getScrollParent(lastElement) : null;

      let noScrollTimeout: ReturnType<typeof setTimeout>;

      const onScrollStart = () => {
        clearTimeout(noScrollTimeout);
      };

      const onScrollComplete = () => {
        if (isInDrawer) {
          firstElement.style.scrollMarginTop = originalScrollMargin;
        }

        wrapper.removeEventListener("scrollend", onScrollComplete);
        wrapper.removeEventListener("scroll", onScrollStart);
        clearTimeout(noScrollTimeout);

        createCutouts();
      };

      wrapper.addEventListener("scrollend", onScrollComplete, { signal });
      wrapper.addEventListener("scroll", onScrollStart, { signal, once: true });

      // The `scrollend` event is not guaranteed to fire if no scrolling
      // actually occurs. This timeout acts as a fallback to ensure
      // `onScrollComplete` is always called.
      noScrollTimeout = setTimeout(onScrollComplete, 150);

      signal.addEventListener("abort", () => {
        if (isInDrawer) {
          firstElement.style.scrollMarginTop = originalScrollMargin;
        }

        clearTimeout(noScrollTimeout);
      });

      // We use `scrollBy` instead of `scrollIntoView` because `scrollIntoView`
      // can have unintended side effects when this component is embedded in an
      // iframe. `scrollBy` ensures that scrolling is contained within the
      // scroll parent of the element.
      const firstRect = firstElement.getBoundingClientRect();

      let viewportCenter: number;
      let viewportTop: number;

      if (wrapper === window) {
        viewportTop = 0;
        viewportCenter = window.innerHeight / 2;
      } else {
        const scrollable = wrapper as HTMLElement;
        const scrollParentRect = scrollable.getBoundingClientRect();

        viewportTop = scrollParentRect.top;
        viewportCenter = viewportTop + scrollParentRect.height / 2;

        if (isInDrawer) {
          const headerHeight = parseFloat(DRAWER_HEADER_HEIGHT);
          const viewportHeight = scrollParentRect.height - headerHeight;

          viewportTop += headerHeight;
          viewportCenter = viewportTop + viewportHeight / 2;
        }
      }

      let scrollAmount: number;
      if (
        lastElement &&
        lastElement !== firstElement &&
        wrapper === lastWrapper
      ) {
        // Same scroll parent: scroll to midpoint.
        const lastRect = lastElement.getBoundingClientRect();
        const midpoint = (firstRect.top + lastRect.bottom) / 2;

        scrollAmount = midpoint - viewportCenter;
      } else {
        // For a single element, or elements in different scroll containers,
        // scroll to the first one. If the element is taller than the
        // viewport, align to the top to prevent its top from being hidden.
        const elementHeight = firstRect.height;

        const scrollParentHeight =
          wrapper === window
            ? window.innerHeight
            : (wrapper as HTMLElement).clientHeight;

        const blockPosition =
          elementHeight > scrollParentHeight ? "start" : "center";

        if (blockPosition === "center") {
          const elementCenter = firstRect.top + elementHeight / 2;

          scrollAmount = elementCenter - viewportCenter;
        } else {
          // "start"
          scrollAmount = firstRect.top - viewportTop;
        }
      }

      wrapper.scrollBy({ top: scrollAmount, behavior: "smooth" });

      window.addEventListener("resize", createCutouts, { signal });
      window.addEventListener("scroll", createCutouts, { signal });
    };

    const findAndInitializeElements = () => {
      const allElementsFound = newEmphasis.every((e) =>
        document.querySelector(`[data-emphasis-identifier="${e}"]`)
      );

      if (allElementsFound) {
        initializeEmphasis();
        return true;
      }

      return false;
    };

    // If elements aren't immediately available, set up a MutationObserver to
    // watch for DOM changes. The check is debounced to prevent running on
    // every single micro-mutation, waiting for a quiet period instead.
    if (!findAndInitializeElements()) {
      let debounceTimeout: ReturnType<typeof setTimeout> | undefined;

      const observer = new MutationObserver(() => {
        clearTimeout(debounceTimeout);

        debounceTimeout = setTimeout(() => {
          if (findAndInitializeElements()) {
            observer.disconnect();
          }
        }, 250);
      });

      observer.observe(document.body, { childList: true, subtree: true });
      signal.addEventListener("abort", () => {
        clearTimeout(debounceTimeout);
        observer.disconnect();
      });
    }
  },
  { immediate: true, flush: "post" }
);
</script>
